<template>
  <div class="notices" ref="notices">

    <!-- Иконка уведомлений -->
    <div class="notices__icon-wrapper" @click="openNotificationsList">
      <div
        v-html="require('@/assets/img/bell.svg')"
        class="notices__icon-image"
        :class="{ active: showList }"
      ></div>
      <div class="notices__icon-dot" v-if="hasUnseenNotifications"></div>
    </div>

    <div class="notices__list-wrapper list" v-show="showList || forceShow">

      <!-- Плавающая шапка -->
      <div class="notices__list-header">

        <!-- Количество уведомлений -->
        <bim-text type=medium bold class="list__count px-9">
          {{ noticesCountText }}
        </bim-text>

        <!-- Множественный выбор уведомлений -->
        <div class="management-buttons pt-6 px-9">
          <bim-checkbox
            v-model="checkAll"
            @input="setCheckToAllNotices"
            id="checkAll"
            class="management-buttons__checkbox mr-2"
          />
          <bim-button
            class="management-buttons__button management-buttons__button--primary mr-6"
            label="Пометить как прочитанное"
            outline
            @click="seeAllCheckedNotifications"
            :disabled="!countCheckedNotices"
          />
          <bim-button
            @click="deleteAllCheckedNotifications"
            class="management-buttons__button management-buttons__button--gray"
            label="Удалить"
            color=gray
            :disabled="!countCheckedNotices"
          />
        </div>
      </div>

      <div v-if="!noticesList.length" class="py-6"></div>

      <!-- Список уведомлений -->
      <div class="list__wrapper">
        <div
          v-for="notice in sortedNoticesList"
          :key="notice.id"
          class="list__item"
          :class="{ seen: notice.seenAt }"
        >
          <bimCheckbox
            v-model="notice.check"
            @click.stop
            :id="notice.id"
            @input="checkForAllNotices"
            class="pl-9"
          />

          <component
            :is="getNoticeComponent(notice).is"
            v-bind="getNoticeComponent(notice).bind"
            v-on="getNoticeComponent(notice).listeners"
            class="list__item-content pr-9"
          >
            <!-- Заголовок -->
            <bim-text type=medium bold class="list__item-title">{{ notice.title }}</bim-text>

            <!-- Описание -->
            <bim-text
              v-if="notice.text"
              type=medium
              class="list__item-description mt-2"
            >{{ notice.text }}</bim-text>

            <div class="list__item-bottom mt-2">
              <!-- Тип уведомления -->
              <!-- <bim-text type=small class="list__item-type mr-4">системное</bim-text> -->

              <!-- Время -->
              <bim-text type=small class="list__item-time">{{ notice.createdAt | noticeCorrectDate }}</bim-text>
            </div>
          </component>

          <div
            v-html="require('@/assets/img/icon-trash-small.svg')"
            class="list__item-delete"
            @click="deleteNotice(notice)"
          ></div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { get } from 'lodash'

import bimText from '@/components/text/bimText/bimText'
import bimCheckbox from '@/components/form/bimCheckbox/bimCheckbox'
import bimButton from '@/components/form/bimButton/bimButton'

import FormPhoneVerification from '@/components/ui/formPhoneVerification/formPhoneVerification'
import NoticeRegion from '@/components/core/bimNotices/notice-region'
import NoticeOccupation from '@/components/core/bimNotices/notice-occupation'
import NoticeDefault from '@/components/core/bimNotices/notice-default'
import NoticeTrialHasExpired from '@/components/core/bimNotices/NoticeTrialHasExpired'

import { closest } from '@/utils/polyfills'
import { declOfNum, pick } from '@/utils/helpers'

const lsKeyAutoShow = 'lsNoticesAutoShow'

export default {
  name: 'bimNotices',
  components: {
    bimText,
    bimCheckbox,
    bimButton
  },
  props: {
    /**
     * passport-service api url
     */
    apiHost: String,

    /**
     * Объект пользователя
     */
    user: Object,

    /**
     * Логирующая функция /log/event
     */
    logActionEvent: {
      type: Function,
      default: () => {}
    },

    /**
     * Свой массив уведомлений
     */
    customNoticesList: {
      type: Array,
      default: null
    },

    /**
     * Насильно показать список
     */
    forceShow: {
      type: Boolean,
      default: null
    },

    /**
     * Делать проверку на новые уведомления
     */
    checkNewNotices: {
      type: Boolean,
      default: true
    },

    /**
     * Timeout для запроса информации
     * о новых нотисах в мс
     */
    checkNewNoticesPeriod: {
      type: Number,
      default: 60000
    }
  },
  data () {
    return {
      checkAll: false,
      showList: false,
      noticesList: [],
      hasNewNotices: false,
      activeNotices: []
    }
  },
  mounted () {
    this.fetchNotices()
      .then(notices => {
        if (!notices) return

        const { href } = window.location
        const noticesWithAutoShowParam = notices.filter(
          notice => (notice.parameters || {}).urlAutoShow
        )

        noticesWithAutoShowParam
          .filter(notice => {
            if (notice.seenAt) return false
            return href.includes(get(notice, 'parameters.urlAutoShow'))
          })
          .forEach(this.handleAutoShowNotice)

        this.localStorageLeaveActualNotices(noticesWithAutoShowParam)
      })

    if (this.customNoticesList) {
      this.noticesList = this.customNoticesList
    }

    this.hideListOnClickOutside = this.hideListOnClickOutside.bind(this)
    this.closeToast = this.closeToast.bind(this)

    /* постоянная проверка на наличие новых нотисов */
    this.checkForNewNotices()
  },
  computed: {
    /**
     * есть ли непросмотренные нотисы
     */
    hasUnseenNotifications () {
      return this.hasNewNotices || this.noticesList.filter(
        notice => !notice.seenAt
      ).length > 0
    },

    /**
     * количество нотисов
     */
    noticesCountText () {
      const count = this.noticesList.length

      if (!count) return 'Нет уведомлений'

      const translations = ['уведомление', 'уведомления', 'уведомлений']
      return `${count} ${declOfNum(count, translations)}`
    },

    /**
     * отсортированный список нотисов по просмотрам
     */
    sortedNoticesList () {
      return [].concat(this.noticesList).sort((a, b) => {
        if (!a.seenAt) return -1
        if (!b.seenAt) return 0
        return b.seenAt - a.seenAt
      })
    },

    /**
     * Количество отмеченных уведомлений
     */
    countCheckedNotices () {
      return this.noticesList
        .filter(n => n.check)
        .length
    }
  },
  watch: {
    customNoticesList (list) {
      this.noticesList = list
    },
    /**
     * при показе запрашивает список нотисов
     *
     * также добавляет/удаляет обработчик по клику вне списка
     * для его скрытия
     */
    showList (show) {
      try {
        if (show) {
          this.fetchNotices()
          setTimeout(() => {
            window.addEventListener('click', this.hideListOnClickOutside)
          })
        } else {
          window.removeEventListener('click', this.hideListOnClickOutside)
        }
      } catch (e) {}
    }
  },
  methods: {
    /**
     * Оставляет в localStorage только
     * актуальные нотисы которые есть
     * у пользователя, сделано чтобы
     * не переполнять хранилище
     */
    localStorageLeaveActualNotices (notices) {
      try {
        localStorage.setItem(
          lsKeyAutoShow,
          pick(
            JSON.parse(localStorage.getItem(lsKeyAutoShow)),
            notices.map(n => n.code)
          )
        )
      } catch (e) {}
    },

    /**
     * определяет каким будет
     * элемент списка нотисов
     * в зависимости от типа
     */
    getNoticeComponent (notice) {
      const urlTransition = get(notice, 'parameters.urlTransition')
      const urlTarget = get(notice, 'parameters.urlTransition')
      const type = get(notice, 'parameters.type')

      if (type === 'transition' && urlTransition) {
        /* rel для ссылок на другие сайты */
        const rel = (
          !urlTransition.match(/\.?bimlib\..*\/?/) && !!urlTransition.indexOf('/')
        ) && 'nofollow'

        return {
          is: 'a',
          bind: {
            href: urlTransition,
            target: urlTarget || '_blank',
            rel
          },
          listeners: {
            click: e => {
              e.stopPropagation()
              if (!notice.seenAt && !this.customNoticesList) {
                this.seeNotice(notice)
              }
            }
          }
        }
      }

      return {
        is: 'div',
        bind: {},
        listeners: {
          click: () => this.showNotice(notice)
        }
      }
    },

    /**
     * Удаление всех отмеченных уведомлений
     */
    deleteAllCheckedNotifications () {
      this.checkAll = false
      this.noticesList.forEach((notice, index, noticesList) => {
        if (!notice.check) return
        this.deleteNotice(notice)
        this.$set(noticesList, index, {
          ...notice,
          check: false
        })
      })
    },

    /**
     * Выставление всем нотисам одного значения
     * @param {Boolean} value
     */
    setCheckToAllNotices (value) {
      this.noticesList.forEach((notice, index, list) => {
        this.$set(list, index, {
          ...notice,
          check: value
        })
      })
    },

    /**
     * Проверка что все галки выставлены
     *
     * если да - отмечаем чекбокс 'выбрать все'
     * иначе его убираем
     */
    async checkForAllNotices () {
      await this.$nextTick()
      if (this.countCheckedNotices < this.noticesList.length) {
        this.checkAll = false
      }
      if (this.countCheckedNotices === this.noticesList.length) {
        this.checkAll = true
      }
    },

    /**
     * Просмотр всех отмеченных нотисов
     */
    seeAllCheckedNotifications () {
      this.checkAll = false
      this.noticesList.forEach((notice, index, noticesList) => {
        if (!notice.check) return

        this.$set(noticesList, index, {
          ...notice,
          check: false
        })

        if (!notice.seenAt) this.seeNotice(notice)
      })
    },

    /**
     * Закрытие тоста нотиса
     */
    closeToast (toast, notice) {
      this.activeNotices.splice(
        this.activeNotices.indexOf(
          notice.code + get(notice, 'parameters.position')
        ),
        1
      )
      if (toast.$el.parentNode) {
        toast.$el.parentNode.removeChild(
          toast.$el.parentNode.childNodes[0]
        )
      }
    },

    /**
     * Проверка но новые нотисы
     */
    checkForNewNotices () {
      if (!this.checkNewNotices) return

      setTimeout(() => {
        this.checkForNewNotices()
      }, this.checkNewNoticesPeriod)

      if (this.hasUnseenNotifications) return

      return fetch(
        `${this.apiHost}/passport/users/me/notifications/unseen`
      )
        .then(r => r.json())
        .then(hasNewNotices => {
          this.hasNewNotices = hasNewNotices
        })
        .catch(e => {
          if (e.status) {
            console.error(`checkForNewNotices [${e.status}] ${e.statusText}`)
          } else {
            console.error(`checkForNewNotices ${e}`)
          }
        })
    },

    /**
     * Открытие списка нотисов с логированием
     */
    openNotificationsList () {
      this.showList = true
      this.logActionEvent('open notice list')
    },

    /**
     * ренден уникального нотиса
     *
     * @param {Object} component - компонент нотиса,
     * который может вызывать события:
     *
     * * close - для скрытия нотиса
     * * save - для сохранения данных
     * * * функция save принимает следующие аргументы -
     * * * @param {String} type [context, user] - что сохранять (поле context или объект пользователя)
     * * * @param {Object} payload - сохраняемый объект
     * * * @param {Function} onFinish - вызывается после завершения сохранения с любым исходом
     *
     * @param {Object} notice - объект нотиса
     *
     * @param {Object} props - дополнительные пропсы для компонента
     */
    renderNotice (component, notice, props = {}, on = {}) {
      const deleteNotice = this.deleteNotice.bind(this)
      const { closeToast, $user } = this
      const toast = this.$toast({
        render: h => h(
          component,
          {
            props,
            on: {
              ...on,
              close () {
                closeToast(toast, notice)
              },
              async save ({ type, payload, onFinish = () => {} }) {
                try {
                  switch (type) {
                    case 'context':
                      await $user.setParams(payload); break
                    case 'user':
                      await $user.updateContext(payload, 'PATCH'); break
                    default:
                      throw new Error(`renderNotice.save unknow type: ${type}`)
                  }
                  deleteNotice(notice)
                } catch (e) {
                  console.error(e)
                } finally {
                  onFinish()
                  closeToast(toast, notice)
                }
              }
            }
          }
        )
      },
      {
        duration: 15000,
        position: get(notice, 'parameters.position', 'bottomEnd'),
        onClose: () => closeToast(toast, notice),
        ...(component.computed?.toastSettings ? component.computed.toastSettings() : {})
      })

      return toast
    },

    /**
     * вызывает события обновления контекста пользователя
     */
    emitUpdateContext (context) {
      this.$emit('updateContext', context)
    },

    /**
     * Обработка просмотра нотиса
     *
     * если он еще не просматривался (нет поля seenAt)
     * отправится запрос на просмотр
     *
     * в зависимости от кода (code) нотиса показывает
     * либо уникальный нотис, либо обычный с текстом
     */
    async showNotice (notice, { seeNotice = true } = {}) {
      const { seenAt, code } = notice

      if (this.activeNotices.includes(code)) return
      this.activeNotices.push(code + get(notice, 'parameters.position'))

      if (seeNotice && !this.customNoticesList && !seenAt) {
        this.seeNotice(notice)
      }

      let toast

      const close = ({ target }) => {
        if (!toast) return
        if (!closest(target, '.bim-toast')) {
          window.removeEventListener('click', close)
          this.closeToast(toast, notice)
        }
      }

      if (code === 'region') {
        toast = this.renderNotice(
          NoticeRegion,
          notice,
          { apiHost: this.apiHost }
        )
      } else if (code === 'occupation') {
        toast = this.renderNotice(
          NoticeOccupation,
          notice,
          { user: this.user }
        )
      } else if (code === 'phone') {
        toast = this.renderNotice(
          FormPhoneVerification,
          notice,
          { user: this.user }
        )
      } else if (code === 'library.demo.access') {
        toast = this.renderNotice(
          NoticeTrialHasExpired,
          notice,
          { user: this.user },
          {
            'open-request-popup': () => this.$emit('open-request-popup')
          }
        )
      } else {
        toast = this.renderNotice(
          NoticeDefault,
          notice,
          { notice },
          {
            click: () => {
              const { urlTransition, type } = notice.parameters || {}

              if (type === 'transition' && urlTransition) {
                this.seeNotice(notice)
                this.closeToast(toast, notice)
              }
            }
          }
        )
      }

      setTimeout(() => {
        window.addEventListener('click', close)
      })
    },

    /**
     * Прячет список уведомлений по клику вне списка
     */
    hideListOnClickOutside ({ target }) {
      if (!closest(target, '.notices__list-wrapper')) {
        this.showList = false
      }
    },

    /**
     * Запрос списка нотисов
     */
    async fetchNotices () {
      this.hasNewNotices = false

      if (this.customNoticesList) return this.customNoticesList

      let notices
      try {
        notices = await fetch(
          `${this.apiHost}/passport/users/me/notifications?size=999`,
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        )
          .then(r => r.json())
          .then(r => r.items)

        this.noticesList = notices.map(notice => {
          notice.seenAt = +(new Date(notice.seenAt))
          return notice
        })

        return notices
      } catch (e) {
        if (e.status) {
          console.error(`fetchNotices [${e.status}] ${e.statusText}`)
        } else {
          console.error('fetchNotices', e)
        }
      }
    },

    /**
     * Просмотр нотиса
     */
    seeNotice (notice) {
      const { id, code } = notice
      this.logActionEvent(`see notice with code ${code || 'empty'}`, { item: notice })
      return fetch(
        `${this.apiHost}/passport/users/me/notifications/${id}/see`,
        {
          method: 'POST'
        }
      )
        .then(({ status, statusText }) => {
          if (status >= 300) {
            this.$toast(
              `Что то пошло не так, попробуйте еще раз [${status}] ${statusText}`,
              { title: 'Ошибка' }
            )
          } else {
            const index = this.noticesList.findIndex(
              notice => notice.id === id
            )
            this.$set(this.noticesList, index, {
              ...notice,
              check: false,
              seenAt: +(new Date())
            })
          }
        })
        .catch(console.error)
    },

    /**
     * Удаление нотиса
     */
    deleteNotice (notice) {
      const { id, seenAt } = notice
      this.logActionEvent('delete notice', { item: notice })
      if (!seenAt) this.logActionEvent('delete unseen notice', { item: notice })
      return fetch(
        `${this.apiHost}/passport/users/me/notifications/${id}/`,
        {
          method: 'DELETE'
        }
      )
        .then(({ status, statusText }) => {
          if (status >= 300) {
            this.$toast(
              `Что то пошло не так, попробуйте еще раз [${status}] ${statusText}`,
              { title: 'Ошибка' }
            )
          } else {
            const index = this.noticesList.findIndex(
              notice => notice.id === id
            )
            this.noticesList.splice(index, 1)
          }
        })
        .catch(console.error)
    },

    handleAutoShowNotice (notice) {
      const { parameters: { countViews }, code } = notice

      let lsNotices
      try {
        lsNotices = JSON.parse(localStorage.getItem(lsKeyAutoShow))
      } catch (e) {}

      if (!lsNotices) lsNotices = {}
      if (!lsNotices[code]) {
        lsNotices[code] = {
          lastSeen: Date.now(),
          count: 0
        }
      }

      const lastSeen = new Date(lsNotices[code].lastSeen)
      const timeToEndOfDay = Date.now() - lastSeen

      /**
       * последний показ был больше
       * суток назад, все сбрасываем
       */
      if (timeToEndOfDay >= 86400000) {
        lsNotices[code].lastSeen = new Date()
        lsNotices[code].count = 0
      }

      /* превышен лимит показов */
      if (lsNotices[code].count >= countViews) return
      else ++lsNotices[code].count

      this.showNotice(notice, { seeNotice: false })
      localStorage.setItem(lsKeyAutoShow, JSON.stringify(lsNotices))
    }
  },
  filters: {
    /**
     * кастомное отображение даты нотиса
     */
    noticeCorrectDate (date) {
      if (!date) return date

      const publishDate = new Date(date)
      const today = new Date()

      /* разница во времени публикации в минутах */
      const diffPublishedMinutes = (today - publishDate) / 1000 / 60

      const startOfCurrenttDay = new Date()
      startOfCurrenttDay.setHours(0, 0, 0, 0)

      /* кол-во часов от создания уведомления до текущего дня */
      const hoursToStartDay = (today - startOfCurrenttDay) / 1000 / 60 / 60

      /* разница во времени публикации в часах */
      const diffPublishedHours = (today - publishDate) / 1000 / 60 / 60

      if (diffPublishedMinutes < 60) {
        return `${parseInt(diffPublishedMinutes)} мин. назад`
      } else if (diffPublishedHours < hoursToStartDay) {
        return `${parseInt(diffPublishedHours)} ч. назад`
      } else {
        const addZero = num => parseInt(num) <= 9 ? '0' + num : num
        return `${addZero(publishDate.getDate())}.${addZero(publishDate.getMonth() + 1)}.${publishDate.getFullYear()}`
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.notices {
  position: relative;

  .notices__icon-dot {
    box-sizing: content-box;
    position: absolute;
    top: 8px;
    right: 8px;
    width: 6px;
    height: 6px;
    border: solid 2px white;
    background-color: #eb3030;
    border-radius: 50%;
  }

  .notices__icon-wrapper {
    &:hover {
      opacity: .75;
    }
  }

  // Иконка
  .notices__icon-image {
    border-radius: 50%;
    transition: all .2s;
    cursor: pointer;
    width: 40px;
    height: 40px;

    ::v-deep {
      svg {
        padding: 10px 11px;
      }

      path {
        transition: fill .2s;
      }
    }

    &:active, &.active {
      background-color: #a6a6a6;

      ::v-deep svg path {
        fill: #ffffff;
      }
    }
  }

  // Контейнер списка уведомлений
  .notices__list-wrapper {
    position: absolute;
    z-index: 1010;
    top: 66px;
    left: -108px;
    width: 436px;
    max-height: 552px;
    height: auto;
    overflow: auto;
    border-radius: 5px;
    box-shadow: 0 20px 40px 0 rgba(0, 0, 0, 0.1);
    background-color: white;
    box-sizing: border-box;

    &::-webkit-scrollbar-track {
      background-color: $white-color-100;
    }

    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-thumb {
      background-color: $white-color-600;
    }

    &:hover::-webkit-scrollbar-thumb {
      background-color: $white-color-700;
    }
  }

  .notices__list-header {
    background-color: white;
    position: sticky;
    top: 0;
    z-index: 1;
    padding: 24px 0;
    border-bottom: 1px solid #f0f0f0;
  }
}

// Список уведомлений
.list {
  .list__count {
    font-family: Helvetica, sans-serif, arial;
  }

  .list__item {
    position: relative;
    display: flex;
    cursor: pointer;
    padding: 14px 0;
    border-bottom: 1px solid #f0f0f0;

    .list__item-description,
    .list__item-title {
      word-break: break-word;
      white-space: break-spaces;
    }

    &.seen {
      opacity: .75;
    }

    &:hover {
      .list__item-delete {
        opacity: 1;
      }
    }

    .list__item-delete {
      position: absolute;
      top: 16px;
      right: 24px;
      opacity: 0;
      transition: opacity .2s;
      padding: 8px 9px 6px;
      background-color: white;
      border-radius: 50%;
      cursor: pointer;
    }

    .list__item-content {
      flex-grow: 1;
      text-decoration: none;
      color: inherit;
      transition: .2s ease;

      &:hover {
        opacity: .6;
      }
    }
  }

  .list__item-bottom {
    display: flex;
  }

  .list__item-type,
  .list__item-time {
    opacity: 0.5;
    font-size: 8px;
    line-height: 1.88;
    letter-spacing: 0.8px;
    text-transform: uppercase;
  }
}

.management-buttons {
  display: flex;
  align-items: center;

  .management-buttons__checkbox {
    height: fit-content;
  }

  .management-buttons__button {
    padding: 8px 0;
    font-size: 12px;
    line-height: 1.67;
    height: auto;
    font-weight: bold;
  }

  .management-buttons__button--primary {
    padding: 6px 0;
    flex-grow: 2;
    border: solid 1px #f0f0f0;
  }

  .management-buttons__button--gray {
    flex-grow: 1;
  }
}
</style>
